// Timeline of a family's versions, newest first: version label, status pill, title, validity window, page count, the run that ingested it (Operator and Admin only) and the lifecycle actions slot for administrators.
import { Link } from "@tanstack/react-router";

import type { DocumentOut } from "@/api/__generated__";
import { useInternalDetails } from "@/app/useInternalDetails";
import { VersionActions } from "@/features/jobs/VersionActions";
import { DocumentStatusBadge } from "@/features/library/DocumentStatusBadge";
import { cn, linkClass } from "@/lib/cn";
import { formatDate, formatNumber } from "@/lib/format";

export function validityText(
  version: Pick<DocumentOut, "status" | "valid_from" | "valid_to" | "published_on">,
): string {
  if (version.valid_from !== null && version.valid_to !== null) {
    return `Valid ${formatDate(version.valid_from)} – ${formatDate(version.valid_to)}`;
  }
  if (version.valid_from !== null) {
    return version.status === "in_force"
      ? `In force since ${formatDate(version.valid_from)}`
      : `Valid from ${formatDate(version.valid_from)}`;
  }
  if (version.published_on !== null) {
    return `Published ${formatDate(version.published_on)}`;
  }
  return "No validity dates recorded";
}

export function VersionTimeline({ versions }: { versions: readonly DocumentOut[] }) {
  const internal = useInternalDetails();
  const newestFirst = [...versions].reverse();
  return (
    <ol className="m-0 flex list-none flex-col p-0" aria-label="Versions, newest first">
      {newestFirst.map((version, index) => (
        <li key={version.doc_uid} className="relative flex gap-3.5 pb-5 last:pb-0">
          <span aria-hidden className="relative flex w-3 shrink-0 justify-center">
            <span
              className={cn(
                "relative z-(--z-sticky) mt-1.5 size-3 rounded-full border-2 bg-card",
                version.in_effect ? "border-brand-red" : "border-b3",
              )}
            />
            {index < newestFirst.length - 1 ? (
              <span className="absolute top-5 bottom-[-6px] w-px bg-border" />
            ) : null}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <span className="font-mono text-ui font-semibold text-foreground">
                  {version.version_label}
                </span>
                <DocumentStatusBadge status={version.status} />
                {version.in_effect && version.status !== "in_force" ? (
                  <span className="text-label font-semibold text-ink-2">Still applies today</span>
                ) : null}
              </div>
              <VersionActions docUid={version.doc_uid} familyId={version.family_id} status={version.status} />
            </div>
            <p className="m-0 mt-1 truncate text-chip text-ink-2">{version.title}</p>
            <p className="m-0 mt-0.5 text-chip text-muted-foreground">
              {validityText(version)}
              {version.page_count === null ? "" : ` · ${formatNumber(version.page_count)} pages`}
            </p>
            {!internal || version.run_uid === null ? null : (
              <p className="m-0 mt-1 text-label text-muted-foreground">
                Ingested by{" "}
                <Link
                  to="/ops/runs/$runUid"
                  params={{ runUid: version.run_uid }}
                  className={cn(linkClass, "font-mono text-label")}
                >
                  {version.run_uid}
                </Link>
              </p>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}
